/**
 * DOMI-CARE - Gestión de Autenticación y Control de Accesos por Rol (RBAC)
 * Cumple con normativas de protección de datos sensibles de salud.
 */

import { SEED_DATA } from '../data/seedData.js';
import { offlineStore } from '../api/offlineStore.js';

// Matriz de permisos por rol
const ROLE_PERMISSIONS = {
  'Admin': {
    canViewHCD: true,
    canEditHCD: true,
    canSignVisits: true,
    canManagePacientes: true,
    canManagePrestadores: true,
    canManageLogistics: true,
    canAudit: true,
    canReportIncidents: true,
    canExportFHIR: true,
    canConfigServer: true
  },
  'Médico Coordinador': {
    canViewHCD: true,
    canEditHCD: true,
    canSignVisits: true,
    canManagePacientes: true,
    canManagePrestadores: true,
    canManageLogistics: true,
    canAudit: true,
    canReportIncidents: true,
    canExportFHIR: true,
    canConfigServer: false
  },
  'Médico Tratante': {
    canViewHCD: true,
    canEditHCD: true,
    canSignVisits: true,
    canManagePacientes: false,
    canManagePrestadores: false,
    canManageLogistics: false,
    canAudit: false,
    canReportIncidents: true,
    canExportFHIR: true,
    canConfigServer: false
  },
  'Enfermería': {
    canViewHCD: true,
    canEditHCD: true,
    canSignVisits: true,
    canManagePacientes: false,
    canManagePrestadores: false,
    canManageLogistics: true, // puede registrar consumo de farmacia
    canAudit: false,
    canReportIncidents: true,
    canExportFHIR: false,
    canConfigServer: false
  },
  'Kinesiología/Rehabilitación': {
    canViewHCD: true,
    canEditHCD: true,
    canSignVisits: true,
    canManagePacientes: false,
    canManagePrestadores: false,
    canManageLogistics: false,
    canAudit: false,
    canReportIncidents: true,
    canExportFHIR: false,
    canConfigServer: false
  },
  'Auditor Médico': {
    canViewHCD: true,
    canEditHCD: false,
    canSignVisits: false,
    canManagePacientes: false,
    canManagePrestadores: false,
    canManageLogistics: false,
    canAudit: true,
    canReportIncidents: false,
    canExportFHIR: true,
    canConfigServer: false
  },
  'Logística/Farmacia': {
    canViewHCD: false,
    canEditHCD: false,
    canSignVisits: false,
    canManagePacientes: false,
    canManagePrestadores: false,
    canManageLogistics: true,
    canAudit: false,
    canReportIncidents: false,
    canExportFHIR: false,
    canConfigServer: false
  }
};

class AuthState {
  constructor() {
    this.currentUser = SEED_DATA.usuarios[0]; // Default: Admin
    this.listeners = [];
    this.init();
  }

  async init() {
    const savedUser = await offlineStore.getSetting('activeUser');
    if (savedUser) {
      this.currentUser = savedUser;
    }
  }

  getActiveUser() {
    return this.currentUser;
  }

  getRole() {
    return this.currentUser ? this.currentUser.rol : 'Guest';
  }

  hasPermission(permission) {
    const role = this.getRole();
    const permissions = ROLE_PERMISSIONS[role] || {};
    return !!permissions[permission];
  }

  getAllUsers() {
    return SEED_DATA.usuarios;
  }

  async switchUser(userId) {
    const user = SEED_DATA.usuarios.find(u => u.id === userId);
    if (user) {
      this.currentUser = user;
      await offlineStore.setSetting('activeUser', user);
      this.notify();
      window.dispatchEvent(new CustomEvent('domicare:user-changed', { detail: user }));
    }
  }

  onUserChange(listener) {
    this.listeners.push(listener);
  }

  notify() {
    this.listeners.forEach(fn => fn(this.currentUser));
  }
}

export const authState = new AuthState();
